import React, { FC, useEffect, useState } from 'react';

import { JiraAuthDetails, JiraCloudTicketCreator, TITLE_MODES } from '@evinced-private/ui-common';

import { JiraApi } from '../../api/JiraApi';
import {
	getJiraAccessToken,
	removeJiraAccessToken,
	setJiraAccessToken
} from '../../api/LocalStorageApi';
import { JIRA_LABELS } from '../../consts/jira-labels';
import { asyncHandler } from '../../helpers/AsyncHelper';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';
import { ScreenshotService } from '../../services/ScreenshotService';
import { JiraCloudData } from '../../types/JiraTypes';
import { Report } from '../../types/ReportModel';
import { SiteScannerPopup } from '../common/site-scanner-popup/SiteScannerPopup';

import './JiraPopup.scss';

const clientID = import.meta.env.VITE_JIRA_APP_CLIENT_ID;
const clientSecret = import.meta.env.VITE_JIRA_APP_CLIENT_SECRET;

interface IJiraIssueCreator {
	isOpen: boolean;
	closeModal: () => void;
	toggleJiraModal: (showModal: boolean) => void;
	ticketData: Report;
	onDisconnect?: () => void;
}
function getAuthDetails(): JiraAuthDetails {
	const jiraToken = JSON.parse(getJiraAccessToken() || '{}');
	return {
		token: jiraToken?.access_token,
		clientId: clientID,
		clientSecret,
		refreshToken: jiraToken?.refresh_token
	};
}

export const JiraPopup: FC<IJiraIssueCreator> = ({
	isOpen,
	closeModal,
	toggleJiraModal,
	ticketData,
	onDisconnect
}) => {
	const [jiraHost, setJiraHost] = useState<string>('');
	const [processedScreenshot, setProcessedScreenshot] = useState<string>(null);

	useEffect(() => {
		const getJiraHost = async (): Promise<void> => {
			const [data] = await asyncHandler<[JiraCloudData]>(
				JiraApi.getJiraCloudId(),
				'Cannot get getJiraCloudId'
			);
			if (data?.[0]) {
				const jiraData = data?.[0];
				setJiraHost(jiraData.url);
			}
		};
		getJiraHost();
	}, []);

	useEffect(() => {
		const generateScreenshotWithBoundingBoxes = async (): Promise<void> => {
			if (ticketData?.pageScreenshot && ticketData?.elements) {
				const boundingBoxes = ticketData.elements.map((element) => element.boundingBox);
				try {
					const screenshotImageData = await ScreenshotService.getFullPageScreenshotWithIssues({
						pageScreenshotUrl: ticketData.pageScreenshot,
						boundingBoxes
					});
					setProcessedScreenshot(screenshotImageData || ticketData.pageScreenshot);
				} catch (error) {
					setProcessedScreenshot(ticketData.pageScreenshot);
				}
			} else {
				setProcessedScreenshot(ticketData?.pageScreenshot || null);
			}
		};
		generateScreenshotWithBoundingBoxes();
	}, [ticketData]);

	const authDetails = getAuthDetails();
	const { getToggle } = useConfiguration();
	const isDefaultLabelsEnabled = getToggle(SiteScannerUiToggles.ENABLE_DEFAULT_JIRA_LABELS);
	const enableDisconnectButton = getToggle(SiteScannerUiToggles.ENABLE_DISCONNECT_FROM_JIRA_BUTTON);
	const isConnected = !!authDetails?.token;
	const showDisconnectButton = enableDisconnectButton && isConnected;

	const initialValues = {
		summary: ticketData.summary,
		description: ticketData.description,
		screenshots: processedScreenshot ? [processedScreenshot] : [ticketData.pageScreenshot],
		...(isDefaultLabelsEnabled && { labels: [JIRA_LABELS.evincedScannerIssue] })
	};

	return (
		<SiteScannerPopup
			title="Create Issue in Jira"
			titleMode={TITLE_MODES.MAIN}
			isControlled
			isCentered
			isOpen={isOpen}
			className="jira-popup"
			onClose={closeModal}
			popupWidth="100%"
		>
			<JiraCloudTicketCreator
				authDetails={authDetails}
				initialValues={initialValues}
				onAbort={() => toggleJiraModal(false)}
				onTokenExpired={() => {
					// Handle token expiration by removing the stored token and closing modal
					removeJiraAccessToken();
					onDisconnect?.(); // Update UI state
					toggleJiraModal(false);
				}}
				onTokenRefreshed={(newTokenData) => {
					// Save the refreshed token data back to localStorage
					setJiraAccessToken(JSON.stringify(newTokenData));
				}}
				onSuccess={(ticketId): void => {
					if (ticketId) {
						window.open(`${jiraHost}/browse/${ticketId}`, '_blank');
						closeModal();
					}
				}}
				onFailure={() => {
					toggleJiraModal(false);
				}}
				showDisconnectButton={showDisconnectButton}
				onDisconnect={() => {
					// Handle manual disconnect: clear tokens and update UI
					removeJiraAccessToken();
					onDisconnect?.(); // Update UI state in parent component
					toggleJiraModal(false);
				}}
			/>
		</SiteScannerPopup>
	);
};
